import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  createStyles,
  Divider,
  Grid,
  ListItem,
  ListItemAvatar,
  ListItemText,
  makeStyles,
  Paper,
  Typography,
} from '@material-ui/core';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import * as React from 'react';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import clsx from 'clsx';
import scan from '../../assets/icons/scan.svg';
import theme from '../../theme';
import WorkReassignmentModal from './reassignmentModal';
import type IRepair from './workReassignmentInterfaces';
import globalUseStyles from '../../globalClasses';

const useStyles = makeStyles(() => createStyles({
  title: {
    fontWeight: 700,
    fontSize: 18,
  },
  repairsScannedSvg: {
    height: 24,
    width: 24,
    marginBottom: 7,
  },
  barcodeCards: {
    height: 70,
    width: 210,
  },
  reassignButton: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
  primaryText: {
    fontSize: '1rem',
    marginBottom: '.125em',
    fontWeight: 700,
  },
  paper: {
    marginTop: '0.75rem',
    width: '100%',
  },
}));

interface IRepairsScannedProps {
  scannedRepairs: IRepair[];
  currentScannedRepair: IRepair;
  setScannedRepairs: React.Dispatch<React.SetStateAction<IRepair[]>>;
  showLandingPage: React.Dispatch<React.SetStateAction<boolean>>;
}

const RepairsScanned: React.FC<IRepairsScannedProps> = (
  {
    scannedRepairs, setScannedRepairs, currentScannedRepair, showLandingPage,
  },
) => {
  const globalStyles = globalUseStyles();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [showReassignModal, setShowReassignModal] = React.useState<boolean>(false);

  const [selectedBin, setSelectedBin] = React.useState<string>('');

  const reassignBin = () => {
    axios.post('/workReassignment/OverrideRepairLocation', {
      ScannedRepairs: scannedRepairs,
      OverrideBinName: selectedBin,
    })
      .then(() => {
        enqueueSnackbar(`${scannedRepairs.length} Repair(s) assigned to ${selectedBin} successfully.`, { variant: 'success' });
        setScannedRepairs([]);
        setShowReassignModal(false);
        showLandingPage(false);
      })
      .catch((e: AxiosError) => {
        enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
      });
  };

  React.useEffect(() => {
    if (currentScannedRepair?.id !== '') {
      setScannedRepairs([...scannedRepairs, currentScannedRepair]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScannedRepair]);

  return (
    <>
      <Grid item xs={12}>
        <Paper>
          <Box p={2}>
            <Grid container justifyContent="space-between">
              <Grid item>
                <img src={scan} alt="scan.svg" className={classes.repairsScannedSvg} />
                <Typography className={classes.title}>{'Repairs scanned in'}</Typography>
              </Grid>
              <Grid item>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item>
                    <Box p={2}>
                      <Typography variant="caption" align="center" color="textSecondary">
                        {`${scannedRepairs.length} REPAIRS`}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item>
                    <Box p={2}>
                      <Button
                        id="complete-dropoff-button"
                        fullWidth
                        variant="contained"
                        color="secondary"
                        disabled={scannedRepairs.length < 1}
                        className={classes.reassignButton}
                        onClick={() => setShowReassignModal(true)}
                      >
                        {'Reassign'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <br />
            <Divider />
            <br />
            <br />
            <Grid container direction="column" alignItems="flex-start" item xs={6} spacing={1}>
              <Box style={{ height: (scannedRepairs.length + 1) * 84 }}>
                {
                scannedRepairs
                  .filter((repair) => repair.shiplog) // Filter repairs with shiplog as true
                  .sort((a, b) => a.barcode.localeCompare(b.barcode)) // Sort by barcode (modify the property if needed)
                  .map((repair) => (
                    <>
                      <Box my={1}>
                        <Paper className={classes.barcodeCards}>
                          <Grid item xs={4}>
                            <ListItem dense disableGutters>
                              <ListItemAvatar style={{ minWidth: '1rem', padding: '0 0.5rem' }}>
                                <Avatar className={clsx(globalStyles.smallAvatar, globalStyles.redIcon)}>
                                  <LocalShippingIcon className={globalStyles.smallIconSize} />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={<Typography variant="h6" color="primary" className={classes.primaryText}>{repair.barcode}</Typography>}
                                secondary={<Typography variant="caption">{'Barcode'}</Typography>}
                              />
                            </ListItem>
                          </Grid>
                        </Paper>
                      </Box>
                    </>
                  ))
                }
                {
                scannedRepairs
                  .filter((repair) => !repair.shiplog) // Filter repairs with shiplog as false
                  .map((repair) => (
                    <Box my={1}>
                      <Card className={classes.barcodeCards}>
                        <CardContent>
                          <Typography color="primary">{`${repair.barcode}`}</Typography>
                          <Typography variant="caption">{'Barcode'}</Typography>
                        </CardContent>
                      </Card>
                    </Box>
                  ))
                }
              </Box>
            </Grid>
          </Box>
        </Paper>
      </Grid>
      <WorkReassignmentModal
        setBin={setSelectedBin}
        showModal={showReassignModal}
        onClose={() => {
          setShowReassignModal(false);
        }}
        handleReassign={reassignBin}
        repairCount={scannedRepairs.length}
      />
    </>
  );
};

export default RepairsScanned;
